import { DatePipe } from '@angular/common';
import { Component, OnInit, Renderer2, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { PrimeNGConfig } from 'primeng/api';
import { RendimientoReport, RendimientoReportFamilia } from '@/app/models/Reporte';
import { ActivosService } from '@/app/services/activos.service';
import { CombustiblesService } from '@/app/services/combustibles.service';
import { EstacionesService } from '@/app/services/estaciones.service';
import { FamiliaequipoService } from '@/app/services/familiaequipo.service';
import { ReportesService } from '@/app/services/reportes.service';
import { TokenReaderService } from '@/app/services/security/token-reader.service';
import { forkJoin, map, of, switchMap } from 'rxjs';

@Component({
    selector: 'app-reportes',
    templateUrl: './reportes.component.html',
    styleUrls: ['./reportes.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportesComponent implements OnInit {

  private spinner = inject(NgxSpinnerService);

  //paginacion de api/reportes/Reportes/Rendimiento: pageSize se topa en 500 del lado del servidor
  private static readonly PAGE_SIZE = 500;
  //tope de peticiones para no dispararnos con rangos de fechas muy amplios
  private static readonly MAX_PAGINAS = 20;
  
  //modelo de busqueda
  searchReportsRQ = {
    startDate: new Date(),
    endDate: new Date(),
    station: this.userTokenReaderService.getUserData().estacion,
    activo: null,
    familia: null,
    tipoCombustible: null,
    page: 1,
    pageSize: ReportesComponent.PAGE_SIZE,
  };

  //total de registros de rendimiento que reporta el servidor para la busqueda actual
  totalRegistros: number = 0;

  //listas
  rendimientoList:RendimientoReport[]=[];
  rendimientoFamiliaList:RendimientoReportFamilia[]=[];

  //validaciones
  isAdmin: boolean = false;
  isReports: boolean = false;
  rendimientoFlag:boolean = false;
  sinCargasXML: boolean = false;
  showXML: boolean = false;

  //catalogos
  estaciones: any[] = [];
  familiasCatalog: any[] =[];
  combustiblesCatalog: any[] =[];
  activosCatalog: any[] = [];

  constructor(
    private userTokenReaderService: TokenReaderService,
    private toastr: ToastrService,
    private router: Router,
    private reportesService: ReportesService,
    private config: PrimeNGConfig,
    private estacionesService: EstacionesService,
    private renderer2: Renderer2,
    private datePipe: DatePipe, private familiaEquipoService: FamiliaequipoService, 
    private combustiblesService: CombustiblesService,private activosService: ActivosService,
  ){
    this.obtenerEstaciones();
    this.obtenerFamilias();
    this.obtenerCombustibles();
    this.obtenerActivos();
    //validaciones
    this.isAdmin = this.userTokenReaderService.isAdmin();
    this.isReports = this.userTokenReaderService.isReportUser();
  }

  ngOnInit(): void {
    this.config.setTranslation ({
      firstDayOfWeek: 1,
      dayNames: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
      dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
      dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
      monthNames: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
      monthNamesShort: ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"],
      today: 'Hoy',
      clear: 'Borrar'
    });
  }

  obtenerEstaciones(){
    this.estacionesService.obtenerEstaciones().subscribe({       
      next: (data: any) => {
        if(data != null && data.Estaciones != null){
          this.estaciones = data.Estaciones.filter( (i: any) => i['IATA'] != '').map( (i: any) => {return {'label': i.IATA, 'value': i.IATA} } );    
        }
      },
      error: (err) => {
       //validacion cuando la lista esta vacia
       this.spinner.hide();
       if(err.status == 401){
         this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
         this.router.navigate(['/login']);
       }else{
        
       this.estaciones = [];
         if(err.error){
           let { ClassInfo } = err.error;
           this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.' , 'Error');
         }
       }
       console.log(err);
      }
    });
  }

  //arma el body que espera api/reportes/Reportes/Rendimiento para la pagina indicada
  private construirRequestBusqueda(page: number){
    return {
      startDate: this.searchReportsRQ.startDate,
      endDate: this.searchReportsRQ.endDate,
      station: this.searchReportsRQ.station ?? '',
      activo: this.searchReportsRQ.activo,
      familia: this.searchReportsRQ.familia,
      tipoCombustible: this.searchReportsRQ.tipoCombustible,
      page: page,
      pageSize: this.searchReportsRQ.pageSize,
    };
  }

  //el rendimiento por equipo llega paginado en Rendimiento
  private obtenerRendimientoDePagina(data: any): RendimientoReport[]{
    return data != null && data.Rendimiento != null ? data.Rendimiento : [];
  }

  private obtenerTotalPaginas(data: any): number{
    let totalPaginas = Number(data?.TotalPages ?? data?.totalPages ?? 1);
    if(!Number.isFinite(totalPaginas) || totalPaginas < 1){
      totalPaginas = 1;
    }
    return totalPaginas;
  }

  buscarConParametros(){
    this.spinner.show();

    if(this.searchReportsRQ.station == null){
      this.searchReportsRQ.station = '';
    }

    this.searchReportsRQ.page = 1;
    this.searchReportsRQ.pageSize = ReportesComponent.PAGE_SIZE;

    let paginasOmitidas = 0;
    //RendimientoFamilia llega completo en cada pagina; solo lo tomamos de la primera
    let rendimientoFamilia: RendimientoReportFamilia[] = [];

    this.reportesService.reporteRendimiento(this.construirRequestBusqueda(this.searchReportsRQ.page)).pipe(
      switchMap((primeraPagina: any) => {
        const rendimiento = this.obtenerRendimientoDePagina(primeraPagina);
        const totalPaginas = this.obtenerTotalPaginas(primeraPagina);

        this.totalRegistros = Number(primeraPagina?.TotalCount ?? rendimiento.length);
        rendimientoFamilia = primeraPagina?.RendimientoFamilia ?? [];

        const paginasAConsultar = Math.min(totalPaginas, ReportesComponent.MAX_PAGINAS);
        paginasOmitidas = totalPaginas - paginasAConsultar;

        if(paginasAConsultar <= 1){
          return of([rendimiento]);
        }

        const peticiones = [];
        for(let page = 2; page <= paginasAConsultar; page++){
          peticiones.push(this.reportesService.reporteRendimiento(this.construirRequestBusqueda(page)));
        }

        return forkJoin(peticiones).pipe(
          map((paginas: any[]) => [rendimiento, ...paginas.map(pagina => this.obtenerRendimientoDePagina(pagina))])
        );
      })
    ).subscribe({
      next: (paginas: RendimientoReport[][]) => {
        this.showXML = true;

        this.rendimientoList = paginas.reduce((acumulado, pagina) => acumulado.concat(pagina), [] as RendimientoReport[]);
        this.rendimientoFamiliaList = rendimientoFamilia ?? [];
        this.searchReportsRQ.page = 1;

        if(this.rendimientoList.length > 0 && this.rendimientoFamiliaList.length > 0){
          this.rendimientoFlag = true;
          this.sinCargasXML = false;
        }else{
          this.rendimientoFlag = false;
          this.sinCargasXML = true;
        }

        this.spinner.hide();

        if(paginasOmitidas > 0){
          this.toastr.info(
            `Se muestran los primeros ${this.rendimientoList.length} de ${this.totalRegistros} registros. Acota el rango de fechas para ver el resto.`,
            'Resultados parciales'
          );
        }
      },
      error: (err) => {
        this.spinner.hide();
        if(err.status == 401){
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']);
        }else{
          if(err.error){
            let { ClassInfo } = err.error;
            this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.' , 'Error');
          }
        }
        console.log(err);
      }
    });
  }
  
  descargarExcel(){
    this.descargarArchivoExcel(this.reportesService.reporteExcelRendimiento(this.searchReportsRQ));
  }

  downloadLectorExcel(){
    this.descargarArchivoExcel(this.reportesService.downloadLectorExcel(this.searchReportsRQ));
  }
  
  private descargarArchivoExcel(request$: import('rxjs').Observable<any>){
    this.spinner.show();
    const fileNamePorDefecto = `Rend${this.searchReportsRQ.station}_${this.datePipe.transform(this.searchReportsRQ.startDate,"yyyy-MM-dd")}_${this.datePipe.transform(this.searchReportsRQ.endDate,"yyyy-MM-dd")}`;

    request$.subscribe({
      next: (data: any) => {
        this.spinner.hide();

        const urlPrefirmada = data?.Url ?? data?.url ?? data?.URL;
        const fileName = data?.FileName ?? data?.fileName ?? fileNamePorDefecto;

        if(urlPrefirmada){
          const a = this.renderer2.createElement("a");
          this.renderer2.setAttribute(a, "href", urlPrefirmada);
          this.renderer2.setAttribute(a, "download", fileName);
          this.renderer2.setAttribute(a, "target", "_blank");
          this.renderer2.selectRootElement(a).click();

          this.borrarReporteDescargado(fileName);
        }else{
          this.toastr.error('La respuesta no contiene la URL del archivo.', 'Error al descargar');
        }
      },
      error: (err: any) => {
        this.spinner.hide();
        if(err.status == 401){
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']);
        }else{
          if(err.error){
            let { ClassInfo } = err.error;
            this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.' , 'Error');
          }
        }
        console.log(err);
      }
    });
  }

  private borrarReporteDescargado(fileName: string){
    if(!fileName){ return; }
    setTimeout(() => {
      this.reportesService.borrarReporte(fileName).subscribe({
        next: () => {
          // console.log('Reporte eliminado de S3:', fileName);
        },
        error: (err: any) => {
          console.log('No se pudo eliminar el reporte de S3:', fileName, err);
        }
      });
    }, 8000);  //margen para que el navegador alcance a descargar el XLSX de S3 antes de borrarlo
  }

  setSeconds(date: Date) {
    if (date) {
      date.setSeconds(0);
      date.setMilliseconds(0);
    }
  }

  obtenerFamilias(){
    this.familiaEquipoService.obtenerFamiliaCatalog().subscribe({       
      next: (data) => {
        this.familiasCatalog = data;
      },
      error: (err) => {
        if(err.status == 401){
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']);
        }else{
          //validacion cuando la lista esta vacia
          this.familiasCatalog = [];
          this.searchReportsRQ.familia = null; 
          if(err.error){
            let { ClassInfo } = err.error;
            this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.' , 'Error');
          }
        }
      }
    });
  }

  obtenerCombustibles() {
    this.combustiblesService.obtenerCombustiblesCatalog().subscribe({
      next: (data) => {
        this.combustiblesCatalog = data;
      },
      error: (err) => {
        if (err.status == 401) {
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']);
        } else {
          this.combustiblesCatalog = [];
          if (err.error) {
            let { ClassInfo } = err.error;
            this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.', 'Error');
          }
        }
      }
    });
  }

  onEstacionChange(){
    this.searchReportsRQ.activo = null;
    this.obtenerActivos();
  }

  obtenerActivos(){
    let busquedaActivo = {
      estacion: this.searchReportsRQ?.station ?? null,
      numActivo: null,
    }

    this.activosService.obtenerActivosCatalog(busquedaActivo).subscribe({       
      next: (data) => {
        this.activosCatalog = data;
      },
      error: (err) => {
        if(err.status == 401){
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']);
        }else{
          //validacion cuando la lista esta vacia
          this.activosCatalog = [];
          this.searchReportsRQ.familia = null; 
          if(err.error){
            let { ClassInfo } = err.error;
            this.toastr.error(ClassInfo ? ClassInfo.Error : 'Comprueba tu conexión a internet.' , 'Error');
          }
        }
      }
    });
  }
}
